'use client'

import ConfirmModal from '@/components/confirm-modal'
import { useState } from 'react'
import {
    MdAdd,
    MdDelete,
    MdEdit,
    MdLocationOn,
    MdStar,
    MdStarBorder,
} from 'react-icons/md'
import AddressFields, {
    AddressFormValue,
    formatAddressLine,
} from './address-fields'

/**
 * Endereço em edição no formulário.
 *
 * `id` ausente = ainda não existe no banco (adicionado agora e não salvo).
 * `_deleted` marca para remoção no próximo salvamento em vez de sumir da lista
 * na hora: o formulário só persiste ao submeter, e apagar de imediato tiraria do
 * usuário a chance de desistir cancelando.
 */
export interface EditableAddress extends AddressFormValue {
    id?: number
    isPrimary?: boolean
    sortOrder?: number
    _deleted?: boolean
}

interface AddressManagerProps {
    value: EditableAddress[]
    onChange: (addresses: EditableAddress[]) => void
    disabled?: boolean
    /** Texto do vazio, para dizer o que aquela entidade representa. */
    emptyHint?: string
}

/**
 * Lista de endereços de uma entidade, com título por unidade.
 *
 * Componente CONTROLADO: não fala com a API. Quem salva é o formulário, ao
 * submeter (ver `syncAddresses`) — assim adicionar um endereço e cancelar o
 * cadastro não deixa lixo gravado, e o comportamento é igual em criação e edição.
 */
export default function AddressManager({
    value,
    onChange,
    disabled = false,
    emptyHint = 'Nenhum endereço cadastrado.',
}: AddressManagerProps) {
    const [editingIndex, setEditingIndex] = useState<number | null>(null)
    const [draft, setDraft] = useState<AddressFormValue>({})
    const [confirmRemove, setConfirmRemove] = useState<number | null>(null)

    const visible = value
        .map((address, index) => ({ address, index }))
        .filter(item => !item.address._deleted)

    const startAdd = () => {
        setDraft({ country: 'BR' })
        setEditingIndex(-1)
    }

    const startEdit = (index: number) => {
        setDraft({ ...value[index] })
        setEditingIndex(index)
    }

    const cancelEdit = () => {
        setEditingIndex(null)
        setDraft({})
    }

    const saveDraft = () => {
        if (editingIndex === null) return

        if (editingIndex === -1) {
            // O primeiro endereço nasce principal: sem isso a entidade ficaria
            // sem pré-seleção no agendamento.
            const isFirst = visible.length === 0
            onChange([
                ...value,
                { ...draft, isPrimary: isFirst, sortOrder: value.length },
            ])
        } else {
            onChange(
                value.map((address, i) =>
                    i === editingIndex ? { ...address, ...draft } : address
                )
            )
        }
        cancelEdit()
    }

    const removeAt = (index: number) => {
        const target = value[index]
        const wasPrimary = !!target.isPrimary

        let next = target.id
            ? // Já existe no banco: marca para o sync apagar ao salvar.
              value.map((a, i) => (i === index ? { ...a, _deleted: true } : a))
            : // Nunca foi salvo: some da lista sem deixar rastro.
              value.filter((_, i) => i !== index)

        // Remover o principal deixaria a entidade sem pré-seleção; promove o
        // primeiro que sobrou.
        if (wasPrimary) {
            const firstAlive = next.findIndex(a => !a._deleted)
            if (firstAlive >= 0) {
                next = next.map((a, i) =>
                    i === firstAlive ? { ...a, isPrimary: true } : a
                )
            }
        }

        onChange(next)
        setConfirmRemove(null)
    }

    const makePrimary = (index: number) => {
        onChange(value.map((a, i) => ({ ...a, isPrimary: i === index })))
    }

    return (
        <div className="flex flex-col gap-3">
            {visible.length === 0 && editingIndex === null && (
                <div className="text-center py-6 border border-dashed border-base-300 rounded-lg">
                    <MdLocationOn
                        size={32}
                        className="mx-auto mb-2 text-base-content/30"
                    />
                    <p className="text-sm text-base-content/60">{emptyHint}</p>
                </div>
            )}

            {visible.map(({ address, index }) => (
                <div
                    key={address.id ?? `novo-${index}`}
                    className="flex items-start gap-3 p-3 rounded-lg border border-base-300/60 bg-base-100"
                >
                    <button
                        type="button"
                        className="btn btn-ghost btn-xs mt-0.5"
                        onClick={() => makePrimary(index)}
                        disabled={disabled || address.isPrimary}
                        title={
                            address.isPrimary
                                ? 'Endereço principal'
                                : 'Definir como principal'
                        }
                    >
                        {address.isPrimary ? (
                            <MdStar className="text-warning" size={18} />
                        ) : (
                            <MdStarBorder
                                className="text-base-content/40"
                                size={18}
                            />
                        )}
                    </button>

                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-base-content">
                                {address.label || 'Sem título'}
                            </span>
                            {address.isPrimary && (
                                <span className="badge badge-sm badge-warning">
                                    Principal
                                </span>
                            )}
                            {!address.id && (
                                <span className="badge badge-sm badge-ghost">
                                    Não salvo
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-base-content/60 mt-0.5">
                            {formatAddressLine(address) || 'Endereço incompleto'}
                        </p>
                        {address.zipCode && (
                            <p className="text-xs text-base-content/50 mt-0.5">
                                CEP {address.zipCode}
                            </p>
                        )}
                    </div>

                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            className="btn btn-ghost btn-xs"
                            onClick={() => startEdit(index)}
                            disabled={disabled}
                            title="Editar"
                        >
                            <MdEdit size={16} />
                        </button>
                        <button
                            type="button"
                            className="btn btn-ghost btn-xs text-error"
                            onClick={() => setConfirmRemove(index)}
                            disabled={disabled}
                            title="Remover"
                        >
                            <MdDelete size={16} />
                        </button>
                    </div>
                </div>
            ))}

            {editingIndex !== null ? (
                <div className="p-4 rounded-lg border border-secondary/40 bg-base-200/30 flex flex-col gap-4">
                    <h4 className="font-medium text-base-content">
                        {editingIndex === -1
                            ? 'Novo endereço'
                            : 'Editar endereço'}
                    </h4>
                    <AddressFields
                        value={draft}
                        onChange={setDraft}
                        showLabel
                        disabled={disabled}
                        idPrefix={`addr-${editingIndex}`}
                    />
                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={cancelEdit}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={saveDraft}
                        >
                            {editingIndex === -1 ? 'Adicionar' : 'Salvar'}
                        </button>
                    </div>
                </div>
            ) : (
                <button
                    type="button"
                    className="btn btn-outline btn-sm gap-2 self-start"
                    onClick={startAdd}
                    disabled={disabled}
                >
                    <MdAdd size={18} />
                    Adicionar endereço
                </button>
            )}

            <ConfirmModal
                isOpen={confirmRemove !== null}
                onClose={() => setConfirmRemove(null)}
                onConfirm={() =>
                    confirmRemove !== null && removeAt(confirmRemove)
                }
                title="Remover endereço"
                message="Tem certeza que deseja remover este endereço? Os agendamentos já confirmados mantêm o endereço que foi informado ao paciente."
                confirmButtonText="Remover"
            />
        </div>
    )
}
